import { SPECIAL_ACTIONS } from '../specialActions';
import type { WorkflowGraph } from './types';

/**
 * Generate a descriptive step ID like "Route_1", "ConvertDocument_2".
 * Special actions get canonical names ("Initial", "Output"); other action
 * IDs are PascalCased and suffixed with `_N` until unique.
 */
export const generateStepId = (actionId: string, existingIds: Set<string>): string => {
  if (actionId === SPECIAL_ACTIONS.initial) return 'Initial';
  if (actionId === SPECIAL_ACTIONS.output) return 'Output';

  const prefix = actionId.charAt(0).toUpperCase() + actionId.slice(1);
  let index = 1;
  let candidate = prefix;
  while (existingIds.has(candidate)) {
    index += 1;
    candidate = `${prefix}_${index}`;
  }

  return candidate;
};

/**
 * Rename a step in the graph: updates the node id, parentId, data.stepId,
 * any `parentStepId` reference, every `StepOutput` binding that referenced
 * the old id, and every edge id / source / target that referenced it.
 */
export const renameStepInGraph = (
  graph: WorkflowGraph,
  oldId: string,
  newId: string,
): WorkflowGraph => {
  const nodes = graph.nodes.map((node) => {
    const isTarget = node.id === oldId;
    const updatedInput = Object.fromEntries(
      Object.entries(node.data.input).map(([key, binding]) => {
        if (binding.kind === 'StepOutput' && binding.stepId === oldId) {
          return [key, { ...binding, stepId: newId }];
        }

        return [key, binding];
      }),
    );
    return {
      ...node,
      id: isTarget ? newId : node.id,
      parentId: node.parentId === oldId ? newId : node.parentId,
      data: {
        ...node.data,
        stepId: isTarget ? newId : node.data.stepId,
        parentStepId: node.data.parentStepId === oldId ? newId : node.data.parentStepId,
        input: updatedInput,
      },
    };
  });

  const edges = graph.edges.map((edge) => {
    const newSource = edge.source === oldId ? newId : edge.source;
    const newTarget = edge.target === oldId ? newId : edge.target;
    const newEdgeId = edge.id.includes(oldId) ? edge.id.split(oldId).join(newId) : edge.id;
    return { ...edge, id: newEdgeId, source: newSource, target: newTarget };
  });

  return { nodes, edges };
};
